/**
 * The plan's storage pool is full (docs/features/billing.md "Enforcement"): an upload came back 403
 * `plan_limit_storage`. What the prompt offers depends on who is asking and where:
 * - owners and admins on a client that sells (web, desktop): "Add storage" (a Stripe subscription: the portal changes
 *   the add-on quantity), "Choose a plan" (no subscription yet: the plan picker, starting with one add-on), or
 *   "Contact us" (every add-on already bought);
 * - owners and admins on iOS and Android (never sell): text only, "manage storage on the web";
 * - a subscription billed by the App Store or Google Play: text only, add storage in that store;
 * - members: who to ask (owners and admins by name).
 * Everyone can also free up room by deleting files.
 */
import type { WorkspaceBilling } from "@bundle/contracts";

/** The most storage add-ons the picker and the stores offer. */
export const MAX_STORAGE_ADDONS = 4;

/** The billing fault codes the API answers with (403); clients re-read the plan on each. */
export type BillingFaultCode = "billing_required" | "plan_limit_members" | "plan_limit_storage";

/** The billing fault an API error code names, or null for any other code. */
export function billingFault(code: string | undefined): BillingFaultCode | null {
  return code === "billing_required" || code === "plan_limit_members" || code === "plan_limit_storage" ? code : null;
}

export type StoragePromptAction = "addStorage" | "choosePlan" | "contact";

export interface StoragePrompt {
  /** The caller is an owner or admin. */
  manage: boolean;
  /** The button (owners and admins on a client that sells, Stripe-billed or no subscription yet). */
  action?: StoragePromptAction;
  /** A store bills the subscription: storage is added there. */
  store?: "app_store" | "play";
  /** "21.4 GB of 30 GB", when both are known. */
  usedBytes?: number;
  poolBytes?: number;
}

export type StoragePromptInput = Pick<WorkspaceBilling, "status" | "storageAddons" | "canManage" | "source"> & {
  currentPeriodEnd?: number | undefined;
  limits: Pick<WorkspaceBilling["limits"], "storageBytes">;
  usage: Pick<WorkspaceBilling["usage"], "storageBytes">;
};

/** The storage-full prompt for this plan (undefined plan: not loaded yet, so a member's prompt without names). */
export function storagePrompt(b: StoragePromptInput | null | undefined, sells: boolean): StoragePrompt {
  if (b === undefined || b === null) return { manage: false };
  const out: StoragePrompt = { manage: b.canManage };
  if (b.usage.storageBytes !== undefined && b.limits.storageBytes !== undefined) {
    out.usedBytes = b.usage.storageBytes;
    out.poolBytes = b.limits.storageBytes;
  }
  if (!b.canManage) return out;
  const subscription = b.status === "active" || b.status === "past_due" || (b.status === "trialing" && b.currentPeriodEnd !== undefined);
  if (subscription && (b.source === "app_store" || b.source === "play")) {
    out.store = b.source;
    return out;
  }
  if (!sells) return out;
  if (b.storageAddons >= MAX_STORAGE_ADDONS) out.action = "contact";
  else out.action = subscription ? "addStorage" : "choosePlan";
  return out;
}

// ---------------------------------------------------------------------------------------------------------------------
// The proactive storage warning (before uploads start failing): Settings → Billing's storage meter and a banner in the
// app chrome's billing row. 80 % of the pool: a warning for owners and admins, dismissible for a day. 95 %: danger,
// members see it too. At or over the pool: full (uploads are refused once enforcement is on). Founding workspaces,
// plans without a pool and an unknown byte figure never warn.
// ---------------------------------------------------------------------------------------------------------------------

/** Where the pool stands: `warn80` from 80 %, `warn95` from 95 %, `full` at or over it. */
export type StorageWarning = "none" | "warn80" | "warn95" | "full";

export const STORAGE_WARN_RATIO = 0.8;
export const STORAGE_DANGER_RATIO = 0.95;
/** A dismissed 80 % banner stays hidden this long. */
export const STORAGE_SNOOZE_MS = 24 * 60 * 60 * 1000;

export type StorageWarningInput = Pick<WorkspaceBilling, "plan"> & {
  limits: Pick<WorkspaceBilling["limits"], "storageBytes">;
  usage: Pick<WorkspaceBilling["usage"], "storageBytes">;
};

/** The pool's warning level (none for founding, no pool, or no byte figure yet). */
export function storageWarning(b: StorageWarningInput | null | undefined): StorageWarning {
  if (b === undefined || b === null || b.plan === "founding") return "none";
  const used = b.usage.storageBytes;
  const pool = b.limits.storageBytes;
  if (used === undefined || pool === undefined || !(pool > 0)) return "none";
  if (used >= pool) return "full";
  if (used >= pool * STORAGE_DANGER_RATIO) return "warn95";
  return used >= pool * STORAGE_WARN_RATIO ? "warn80" : "none";
}

/** The storage banner in the app chrome (shown only when no billing banner takes the row). */
export interface StorageBanner {
  kind: "storage";
  level: Exclude<StorageWarning, "none">;
  /** `warning` at 80 %, `danger` from 95 %. */
  tone: "warning" | "danger";
  /** Whole percent of the pool used, at most 100. */
  percent: number;
  usedBytes: number;
  poolBytes: number;
  /** The caller is an owner or admin. */
  manage: boolean;
  /** Only the 80 % banner can be dismissed (for a day). */
  dismissible: boolean;
  /** Owners and admins on a client that sells: the storage-full prompt's button (logic `storagePrompt`). */
  action?: StoragePromptAction;
  /** A store bills the subscription: storage is added there. */
  store?: "app_store" | "play";
}

/**
 * The storage banner for this caller, or null. Owners and admins from 80 % (dismissible until `snoozedUntil` while
 * under 95 %), members from 95 % (who to ask). Native (`sells` false) has no button.
 */
export function storageBanner(b: (StoragePromptInput & StorageWarningInput) | null | undefined, now: number, sells: boolean, snoozedUntil?: number): StorageBanner | null {
  if (b === undefined || b === null) return null;
  const level = storageWarning(b);
  if (level === "none") return null;
  if (level === "warn80" && (!b.canManage || (snoozedUntil !== undefined && now < snoozedUntil))) return null;
  const used = b.usage.storageBytes!;
  const pool = b.limits.storageBytes!;
  const prompt = storagePrompt(b, sells);
  const out: StorageBanner = {
    kind: "storage",
    level,
    tone: level === "warn80" ? "warning" : "danger",
    percent: Math.min(100, Math.floor((used / pool) * 100)),
    usedBytes: used,
    poolBytes: pool,
    manage: b.canManage,
    dismissible: level === "warn80",
  };
  if (prompt.action !== undefined) out.action = prompt.action;
  if (prompt.store !== undefined) out.store = prompt.store;
  return out;
}
